import { fetchJSON as fetchAPI } from './client';

export type OpsHubDashboard = {
  tenant_id: string;
  open_tasks: number;
  completed_tasks: number;
  blocked_tasks: number;
  overdue_tasks: number;
  waiting_approval: number;
  active_teams: number;
  avg_execution_velocity: number;
  operational_sla_pct: number;
  active_recommendations: number;
  unread_notifications: number;
  generated_at?: string;
};

export type TaskRecord = {
  id: string;
  title: string;
  description?: string;
  domain: string;
  status: string;
  source: string;
  priority: number;
  assignee_id?: string;
  assignee_name?: string;
  team_id?: string;
  due_date?: string;
  estimated_hours: number;
  actual_hours: number;
  checklist_total: number;
  checklist_done: number;
  linked_release?: string;
  source_module?: string;
  requires_approval: boolean;
  is_overdue: boolean;
  created_at?: string;
};

export type TeamRecord = {
  id: string;
  name: string;
  department?: string;
  member_count: number;
  open_tasks: number;
  capacity_pct: number;
  members?: string[];
};

export type CommentRecord = {
  id: string;
  task_id: string;
  author: string;
  content: string;
  mentions?: string[];
  created_at?: string;
};

export type WorkloadMetrics = {
  id: string;
  team_id?: string;
  team_name?: string;
  open_tasks: number;
  completed_tasks: number;
  blocked_tasks: number;
  overdue_tasks: number;
  capacity_pct: number;
  utilization_pct: number;
  execution_velocity: number;
  operational_sla_pct: number;
};

export type TaskRecommendation = {
  id: string;
  title: string;
  description?: string;
  domain?: string;
  recommended_owner?: string;
  suggested_due_date?: string;
  estimated_hours: number;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type NotificationRecord = {
  id: string;
  task_id?: string;
  type: string;
  title: string;
  message?: string;
  read: boolean;
  created_at?: string;
};

export type CreateTaskRequest = {
  title: string;
  description?: string;
  domain: string;
  priority?: number;
  assignee_id?: string;
  team_id?: string;
  due_date?: string;
  estimated_hours?: number;
};

const API_BASE = import.meta.env.VITE_OPSHUB_API ?? '/api/v1/opshub';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const opshubApi = {
  dashboard: () => fetchJSON<OpsHubDashboard>('/dashboard'),
  listTasks: () => fetchJSON<TaskRecord[]>('/tasks'),
  createTask: (body: CreateTaskRequest) =>
    fetchJSON<TaskRecord>('/tasks', { method: 'POST', body: JSON.stringify(body) }),
  listTeams: () => fetchJSON<TeamRecord[]>('/teams'),
  listComments: (taskId?: string) =>
    fetchJSON<CommentRecord[]>(taskId ? `/comments?task_id=${taskId}` : '/comments'),
  listWorkload: () => fetchJSON<WorkloadMetrics[]>('/workload'),
  listRecommendations: () => fetchJSON<TaskRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<TaskRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listNotifications: () => fetchJSON<NotificationRecord[]>('/notifications'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
